import { useState, useRef, useEffect } from "react";
import { HexColorPicker } from "react-colorful";

interface ColorFieldProps {
  label: string;
  value: string | undefined;
  onChange: (value: string) => void;
  description?: string;
}

export function ColorField({ label, value, onChange, description }: ColorFieldProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showTip, setShowTip] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const displayValue = typeof value === "string" ? value : "#000000";

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  return (
    <div className="group/field rounded-radius-sm px-1.5 py-1 transition-colors hover:bg-surface-sunken/50">
      <div className="flex items-center gap-2">
        <div
          className="relative flex w-28 shrink-0 items-center gap-1"
          onMouseEnter={() => { if (description) setShowTip(true); }}
          onMouseLeave={() => setShowTip(false)}
        >
          <span className="truncate text-[11px] text-text-secondary">{label}</span>
          {description && (
            <span className="text-[9px] text-text-muted/40 opacity-0 transition-opacity group-hover/field:opacity-100">?</span>
          )}
          {showTip && description && (
            <div className="absolute left-0 bottom-full z-50 mb-1 w-52 rounded-radius-sm border border-border-subtle bg-surface-overlay px-2.5 py-1.5 text-[10px] leading-relaxed text-text-secondary shadow-md">
              {description}
            </div>
          )}
        </div>
        <div className="relative flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="h-6 w-6 shrink-0 rounded-radius-sm border border-border-muted shadow-sm transition-shadow hover:shadow-md"
            style={{ backgroundColor: displayValue }}
            title={displayValue}
          />
          <input
            type="text"
            value={displayValue}
            onChange={(e) => onChange(e.target.value)}
            className="h-7 w-20 rounded-radius-sm border border-border-subtle bg-surface-base px-2 text-[11px] text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/20"
          />
          {isOpen && (
            <div
              ref={popoverRef}
              className="absolute top-8 left-0 z-50 rounded-radius-md border border-border-subtle bg-surface-overlay p-2 shadow-lg"
            >
              <HexColorPicker color={displayValue} onChange={onChange} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
